import { gql, useQuery } from '@apollo/client';

/**
 * RxPrint shipping lanes. The warehouse user picks the lane they are working at on the Sales
 * Orders page; the server stores it per user, so every document their pick, verify, and ship
 * actions produce goes to that lane's printers, and package weights come from its scale.
 */

export interface WarehouseShippingLane {
  locationId: string;
  locationName?: string | null;
  number: number;
  label: string;
  hasScale: boolean;
}

export interface MyShippingLane {
  locationId: string;
  laneNumber: number;
  label: string;
}

export const WAREHOUSE_SHIPPING_LANES = gql`
  query warehouseShippingLanes($companyId: ID!) {
    warehouseShippingLanes(companyId: $companyId) {
      locationId
      locationName
      number
      label
      hasScale
    }
    myShippingLane(companyId: $companyId) {
      locationId
      laneNumber
      label
    }
  }
`;

export const SET_MY_SHIPPING_LANE = gql`
  mutation setMyShippingLane($companyId: ID!, $locationId: ID, $laneNumber: Int) {
    setMyShippingLane(companyId: $companyId, locationId: $locationId, laneNumber: $laneNumber) {
      locationId
      laneNumber
      label
    }
  }
`;

export const MY_SHIPPING_LANE_SCALE_READING = gql`
  query myShippingLaneScaleReading($companyId: ID!) {
    myShippingLaneScaleReading(companyId: $companyId) {
      laneNumber
      pounds
      ageMs
    }
  }
`;

export const NO_LANE = 'none';
export const NO_LANE_LABEL = 'No lane';

/** "Lane 2", or "Lane 2 · Austin" when the company ships from more than one location. */
export const laneChoiceLabel = (
  lane: { number: number; locationName?: string | null },
  showLocation: boolean
) => {
  const base = `Lane ${lane.number}`;
  if (!showLocation || !lane.locationName) return base;
  const place = lane.locationName.split('—').pop()?.split(',')[0]?.trim();
  return place ? `${base} · ${place}` : base;
};

export const laneValue = (lane: { locationId: string; number?: number; laneNumber?: number }) =>
  `${lane.locationId}:${lane.number ?? lane.laneNumber}`;

/** The selected lane, if it still exists. */
export const selectedLane = (
  lanes: WarehouseShippingLane[],
  mine: MyShippingLane | null | undefined
): WarehouseShippingLane | null =>
  (mine && lanes.find((lane) => laneValue(lane) === laneValue(mine))) || null;

/** Scale readings fill the weight field to the hundredth of a pound. */
export const scaleWeight = (pounds: number) => Math.round(pounds * 100) / 100;

/**
 * The weight on the user's lane scale, read every second while `active`. `reported` turns true once
 * the first answer arrives; `pounds` stays null while the scale is not reporting.
 */
export function useLaneScaleReading(companyId: string | undefined, active: boolean) {
  const { data } = useQuery(MY_SHIPPING_LANE_SCALE_READING, {
    variables: { companyId },
    skip: !active || !companyId,
    pollInterval: 1000,
    fetchPolicy: 'network-only'
  });
  const reading = data?.myShippingLaneScaleReading ?? null;
  return {
    reported: active && data !== undefined,
    pounds: (reading?.pounds ?? null) as number | null
  };
}
